import * as React from 'react';
import {Props} from "./types";
import {CarrierSelector} from "./carrier-selector";
import { useMedigapCompaniesQuery } from '@coverright/data-access/types/medigap';
import { mainBackendApolloClient } from '@coverright/data-access/apollo-clients';
import { isEffectiveDateValid } from '@coverright/utils';
import { Gender } from '@coverright/data-access/types/enrollment';
import { Tooltip } from '@coverright/ui/inputs';
import { Box } from '@mui/material';
import { useMemo } from 'react';

type CarrierSelectorProps = Omit<Props, 'planYear' | 'onChange'> & {
  effectiveDate?: string,
  age: number,
  tobacco?: boolean,
  gender?: Gender,
  onChange: (data: {naic: string, name?: string}) => void,
}

export const MgCarrierSelector = (props: CarrierSelectorProps) => {
  const {zip, county, age, effectiveDate, gender, tobacco, onChange} = props;

  const {data, loading} = useMedigapCompaniesQuery({
    client: mainBackendApolloClient,
    variables: {
      filters: {
        zip,
        county,
        age,
        effectiveDate,
        tobacco: tobacco!,
        companies: [],
        gender: gender!,
        monthlyPlanPremiumRanges: [],
        planNames: [],
      }
    },
    skip: !county || !zip || !isEffectiveDateValid(effectiveDate) || !age || !gender || typeof tobacco !== 'boolean'
  })

  const helperTip = useMemo(() => {
    const fields = [];
    if (!zip || !county) {
       fields.push('zip')
     }
    if (!age) {
      fields.push('age')
    }
    if (!isEffectiveDateValid(effectiveDate)) {
      fields.push('effective date')
    }
    if (!gender) {
      fields.push('gender')
    }
    if (typeof tobacco !== 'boolean') {
      fields.push('tobacco')
    }

    if (fields.length) {
      return `You have to fill ${fields.join(', ')} to select carrier`
    }

    return ''
  }, [zip, county, age, effectiveDate, gender, tobacco])


  const companies = data?.medigapCompanies
      .filter(c => c.count)
      .map(c => ({...c, title: `${c.name} (${c.count})`}))
    || [];

  const handleChange = (key: string) => {
    const company = companies.find(c => c.name === key);
    onChange({
      naic: company?.naics[0] || '', name: key
    })
  }

  return <Tooltip sx={{flex: 1, display: 'flex'}} title={helperTip}>
    <Box>
      <CarrierSelector {...props}
                       label={loading ? 'Loading...' : undefined}
                       onChange={handleChange}
                       loading={loading}
                       disabled={props.disabled || loading}
                       companies={companies.map(c => c.title)}  />
    </Box>
  </Tooltip>
};
